// ============================================================================
// authFetch — Security step 7a (Sept 2026)
//
// Adds "Authorization: Bearer <Firebase ID token>" to fetch calls to a SHORT,
// EXPLICIT list of Cloud Functions that now check who is calling:
//
//   closeServiceRequest, PW_PostStorageCharge, sendInboundNotification,
//   PW_INTERNAL_ManualOnboard_v2
//
// Why a fetch wrapper instead of editing each caller: the callers live in
// large, actively edited files (InboundShipments.vue, ClientManagement.vue, ...).
// This keeps the change to two small files. A later cleanup can replace it with
// explicit headers at each call site.
//
// It touches nothing else. Calls to any other URL, including every other
// Cloud Function, Keepa and Amazon, go through unchanged. Several of those
// functions don't allow an Authorization header in their CORS settings, so
// adding one would break them. That's why this uses an allowlist.
//
// Who the token belongs to:
//   warehouse.prepworx.io -> the worker signed in by warehouseLogin (role employee/admin)
//   app.prepworx.io       -> the signed-in client (used by DiscordV2 onboarding)
// If nobody is signed in, the request goes out exactly as before. Until step 7b
// the backend ignores this header, and after 7b it only logs until step 8.
// ============================================================================
// Oct 22, 2026 (staff key, step 2): on the WAREHOUSE site, every call to our
// Cloud Functions now carries the worker's login, not just the list below. The
// backend already treats a logged-in worker/admin the same as the staff key
// (staffKeyOr / checkStaffAccess, Sept 29). Oct 28: the warehouse pages no longer
// send the hard-coded key (removed in the Oct 27 staff-key batches), so this login
// is what the backend checks. Every function allows the Authorization
// header (cors({origin:true}) reflects it; the hand-written CORS ones list it).
// The client app (app.prepworx.io) keeps the short list.
import { getAuth, onAuthStateChanged } from 'firebase/auth'

const FN_BASE = 'https://us-central1-prepworx-app.cloudfunctions.net/'

const IS_WAREHOUSE = typeof window !== 'undefined' && window.location.hostname.startsWith('warehouse.')

const AUTH_FUNCTIONS = new Set([
  'closeServiceRequest',
  'PW_PostStorageCharge',
  'sendInboundNotification',
  'sendOutboundNotification',   // Sept 29: outbound activity email
  'PW_INTERNAL_ManualOnboard_v2',
  'keepaBulkLookup',            // Oct 1: now staff-only; inbound + packing pages don't send the key
  'manualBillBalance',          // Oct 1: Client Management "Manually Bill"
  'addClientWarehouse',         // Oct 21: Discord channel at another warehouse (Settings + Client Management)
  'getInventoryTags',           // Nov 3, 2026: client tags + notes per product (inventoryTags.js)
  'setInventoryTags',
  'addInventoryTags',
])

// Firebase 9.21 has no auth.authStateReady(), so wait for the first auth event.
function authReady(auth) {
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, () => { unsub(); resolve() }, () => { unsub(); resolve() })
  })
}

export function installAuthFetch(firebaseApp) {
  if (typeof window === 'undefined' || window.__pwAuthFetchInstalled) return
  window.__pwAuthFetchInstalled = true

  const auth = getAuth(firebaseApp)
  const ready = authReady(auth)
  const originalFetch = window.fetch.bind(window)

  window.fetch = async (input, init) => {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || ''
      if (url.startsWith(FN_BASE)) {
        const name = url.slice(FN_BASE.length).split(/[/?#]/)[0]
        if (IS_WAREHOUSE || AUTH_FUNCTIONS.has(name)) {
          await ready
          const user = auth.currentUser
          if (user) {
            const token = await user.getIdToken()
            const headers = new Headers((init && init.headers) || (typeof input !== 'string' && input && input.headers) || undefined)
            if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`)
            init = { ...(init || {}), headers }
          }
        }
      }
    } catch (e) {
      // Never block the request because of this wrapper.
      console.warn('[authFetch] could not attach token:', e && e.message)
    }
    return originalFetch(input, init)
  }
}
